import type { IHosRuleset } from "hos-tools";
import { ActionsUnion, createAction } from "../definitions";
import {
    DataActionTypes,
    WorkdayType,
    IIoxActionDictionary,
    ITempAssets,
    IImages,
    ICachedAddress,
    TELDJurisdiction,
    IAlertsAvailability,
    IFeatureFlagStatus,
    IActiveEvents,
    ActiveEventType,
    NativeMotionState
} from "./types";

export interface IInspectionPinPayload {
    userId?: string;
    inspectionPin: string;
}

export interface IAlertAvailabilityPayload {
    userId: string;
    alertsAvailability: IAlertsAvailability;
}

// { FeatureFlagName: { name: FeatureFlagName, isEnabled: true } }
export interface IFeatureFlagPayload {
    [ id: string ]: IFeatureFlagStatus;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const DataActions = {
    setBoundaryLogDate: (payload: IIdDictionary<string>) => createAction(DataActionTypes.SET_BOUNDARY_LOG_DATE, payload),
    clearBoundaryLogDate: (payload: string) => createAction(DataActionTypes.CLEAR_BOUNDARY_LOG_DATE, payload),
    setNightMode: (payload: boolean) => createAction(DataActionTypes.SET_NIGHTMODE, payload),
    setPipMode: (payload: boolean) => createAction(DataActionTypes.SET_PIPMODE, payload),
    setClockDrift: (payload: number) => createAction(DataActionTypes.SET_CLOCK_DRIFT, payload),
    setWorkday: (payload: IIdDictionary<WorkdayType>) => createAction(DataActionTypes.SET_WORKDAY, payload),
    clearWorkday: (payload: string) => createAction(DataActionTypes.CLEAR_WORKDAY, payload),
    setIoxLogAction: (payload?: IIoxActionDictionary) => createAction(DataActionTypes.SET_IOX_LOG_ACTION, payload),
    setCurrentHardwareId: (payload?: string) => createAction(DataActionTypes.SET_IOX_HARDWAREID, payload),
    setTempAssets: (payload: IIdDictionary<ITempAssets>) => createAction(DataActionTypes.SET_TEMP_ASSETS, payload),
    clearTempAssets: (payload: string) => createAction(DataActionTypes.CLEAR_TEMP_ASSETS, payload),
    setDtcNextRun: (payload: IIdDictionary<string>) => createAction(DataActionTypes.SET_DTCNEXTRUN, payload),
    clearDtcNextRun: (payload: string) => createAction(DataActionTypes.CLEAR_DTCNEXTRUN, payload),
    setImages: (payload?: IImages) => createAction(DataActionTypes.SET_IMAGES, payload),
    setAcl: (payload: IIdDictionary<string[]>) => createAction(DataActionTypes.SET_ACL, payload),
    clearAcl: (payload: string) => createAction(DataActionTypes.CLEAR_ACL, payload),
    clearAddressCache: (payload: Date) => createAction(DataActionTypes.CLEAR_ADDRESS_CACHE, payload),
    setAddressCache: (payload: { [coordinates: string]: ICachedAddress }) => createAction(DataActionTypes.SET_ADDRESS_CACHE, payload),
    setRuleSetsCache: (payload: IHosRuleset[]) => createAction(DataActionTypes.SET_RULESETS_CACHE, payload),
    setELDJurisdiction: (payload: TELDJurisdiction) => createAction(DataActionTypes.SET_ELD_JURISDICTION, payload),
    setDatabaseVersion: (payload: string) => createAction(DataActionTypes.SET_DATABASE_VERSION, payload),
    setSourceCodeVersion: (payload: string) => createAction(DataActionTypes.SET_SOURCE_CODE_VERSION, payload),
    setServerFlags: (payload: number) => createAction(DataActionTypes.SET_SERVER_FLAGS, payload),
    setDebugMode: (payload: boolean) => createAction(DataActionTypes.SET_DEBUGMODE, payload),
    setLastDatabase: (payload: string) => createAction(DataActionTypes.SET_LAST_DATABASE, payload),
    clearLastDatabase: () => createAction(DataActionTypes.CLEAR_LAST_DATABASE),
    setGpsAccuracyAverage: (payload: number) => createAction(DataActionTypes.SET_GPS_ACCURACY_AVERAGE, payload),
    setLastUser: (payload: string) => createAction(DataActionTypes.SET_LAST_USER, payload),
    setLastServer: (payload: string) => createAction(DataActionTypes.SET_LAST_SERVER, payload),
    clearLastServer: () => createAction(DataActionTypes.CLEAR_LAST_SERVER),
    setInputHints: (payload: IIdDictionary<string[]>) => createAction(DataActionTypes.SET_INPUT_HINTS, payload),
    clearInputHints: (payload: string) => createAction(DataActionTypes.CLEAR_INPUT_HINTS, payload),
    setGroupSecurityId: (payload: ISecurityGroup[]) => createAction(DataActionTypes.SET_GROUP_SECURITY_ID, payload),
    clearGroupSecurityId: () => createAction(DataActionTypes.CLEAR_GROUP_SECURITY_ID),
    setInspectionPin: (payload: IInspectionPinPayload) => createAction(DataActionTypes.SET_INSPECTION_PIN, payload),
    clearInspectionPin: (payload?: string) => createAction(DataActionTypes.CLEAR_INSPECTION_PIN, payload),
    setAlertAvailability: (payload: IAlertAvailabilityPayload) => createAction(DataActionTypes.SET_ALERTS_AVAILABILITY, payload),
    clearAlertAvailability: () => createAction(DataActionTypes.CLEAR_ALERTS_AVAILABILITY),
    setFeatureFlags: (payload: IFeatureFlagPayload) => createAction(DataActionTypes.SET_FEATURE_FLAGS, payload),
    setFeatureFlagOverride: (payload: { flagName: string; isEnabled: boolean }) => createAction(DataActionTypes.SET_FEATURE_FLAG_OVERRIDE, payload),
    clearFeatureFlagOverride: (payload: string) => createAction(DataActionTypes.CLEAR_FEATURE_FLAG_OVERRIDE, payload),
    clearAllFeatureFlagOverrides: () => createAction(DataActionTypes.CLEAR_ALL_FEATURE_FLAG_OVERRIDES),
    setLastLoginDate: (payload: {[userName: string]: string}) => createAction(DataActionTypes.SET_LAST_LOGIN_DATE, payload),
    clearlastLoginDate: (payload: string) => createAction(DataActionTypes.CLEAR_LAST_LOGIN_DATE, payload),
    setEnableSyncDelay: (payload: boolean) => createAction(DataActionTypes.SET_ENABLE_SYNC_DELAY, payload),
    setInstanceId: (payload: string) => createAction(DataActionTypes.SET_INSTANCE_ID, payload),
    setDriving: (payload?: boolean) => createAction(DataActionTypes.SET_DRIVING, payload),
    setNativeMotion: (payload?: NativeMotionState) => createAction(DataActionTypes.SET_NATIVE_MOTION, payload),
    setHandledSdcEndedPcLogId: (payload: IIdDictionary<string>) => createAction(DataActionTypes.SET_HANDLED_SDC_ENDED_PC_LOG_ID, payload),
    setVehicleLocation: (payload?: ICoordinate) => createAction(DataActionTypes.SET_VEHICLE_LOCATION, payload),
    addActiveEvent: (payload: IActiveEvents) => createAction(DataActionTypes.ADD_ACTIVE_EVENT, payload),
    removeActiveEvent: (payload: ActiveEventType) => createAction(DataActionTypes.REMOVE_ACTIVE_EVENT, payload),
    clearAllActiveEvents: () => createAction(DataActionTypes.CLEAR_ALL_ACTIVE_EVENTS)
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DataActions = ActionsUnion<typeof DataActions>;
